/**
 * Editor/preview sample for `{{invoice.payment.schedule}}`.
 * Author content keeps the merge-tag token; Unlayer shows this HTML as a non-editable sample.
 * Real installments freeze at share into DocumentShare.finalizedSnapshot (API renderer).
 *
 * Intentionally free of `@talleflow/content-sanitizer` / jsdom so the main
 * `@talleflow/merge-tags` barrel stays safe for Trigger.dev worker imports.
 * Escaped-table restore lives in `./restoreEscapedPaymentScheduleTables`
 * (`@talleflow/merge-tags/restore-payment-schedule`).
 */

/** Picker + Unlayer friendly label (never raw `{{…}}`). */
export const PAYMENT_SCHEDULE_BREAKDOWN_LABEL = 'Payment Schedule Breakdown';

/** Match API `PAYMENT_SCHEDULE_DUE_UPON_SIGNING_TEXT` for editor sample rows. */
export const PAYMENT_SCHEDULE_DUE_UPON_SIGNING_SAMPLE = 'Due upon signing';

/** Renderer-owned marker so restore only decodes our schedule tables. */
export const PAYMENT_SCHEDULE_TABLE_MARKER_ATTR = 'data-tf-payment-schedule';

const TABLE_STYLE =
  'width:100%;border-collapse:collapse;font-size:13px;line-height:1.4;color:#111827;';
const TH_STYLE =
  'text-align:left;padding:6px 8px;border-bottom:1px solid #e5e5e5;color:#6b7280;font-weight:600;';
const TD_STYLE =
  'text-align:left;padding:6px 8px;border-bottom:1px solid #f3f4f6;vertical-align:top;';

function escapeHtmlText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export interface PaymentSchedulePreviewRow {
  readonly label: string;
  readonly amount: string;
  readonly dueDate: string;
}

/**
 * Email-safe inline-styled table from pre-formatted rows (label / amount / due date).
 * Cell text is escaped; tags themselves are authored markup for Unlayer preview / FE samples.
 */
export function renderPaymentSchedulePreviewHtml(
  rows: ReadonlyArray<PaymentSchedulePreviewRow>
): string {
  if (rows.length === 0) {
    return '';
  }

  const body = rows
    .map(
      (row) =>
        `<tr>` +
        `<td style="${TD_STYLE}">${escapeHtmlText(row.label)}</td>` +
        `<td style="${TD_STYLE}">${escapeHtmlText(row.amount)}</td>` +
        `<td style="${TD_STYLE}">${escapeHtmlText(row.dueDate)}</td>` +
        `</tr>`
    )
    .join('');

  return (
    `<table ${PAYMENT_SCHEDULE_TABLE_MARKER_ATTR}="1" style="${TABLE_STYLE}" border="0" cellpadding="0" cellspacing="0" width="100%">` +
    `<thead><tr>` +
    `<th style="${TH_STYLE}">Payment</th>` +
    `<th style="${TH_STYLE}">Amount</th>` +
    `<th style="${TH_STYLE}">Due date</th>` +
    `</tr></thead>` +
    `<tbody>${body}</tbody>` +
    `</table>`
  );
}

/**
 * Sample rows when the document has no linked invoice yet.
 * Shown in Unlayer preview only; design keeps `{{invoice.payment.schedule}}`.
 */
export const PAYMENT_SCHEDULE_EDITOR_SAMPLE_HTML = renderPaymentSchedulePreviewHtml([
  {
    label: 'Payment 1',
    amount: '$500.00',
    dueDate: PAYMENT_SCHEDULE_DUE_UPON_SIGNING_SAMPLE,
  },
  {
    label: 'Payment 2',
    amount: '$500.00',
    dueDate: 'June 1, 2026',
  },
  {
    label: 'Payment 3',
    amount: '$500.00',
    dueDate: 'July 1, 2026',
  },
]);

const SCHEDULE_KEY_RE = /^invoice(?:\.\d+)?\.payment\.schedule$/;

/** True for unpositioned or positional schedule keys. */
export function isPaymentScheduleMergeTagKey(key: string): boolean {
  return SCHEDULE_KEY_RE.test(key);
}

/**
 * Preview sample for a schedule key: live HTML when provided, otherwise the static sample table.
 */
export function paymentScheduleEditorSample(previewHtml?: string | null): string {
  if (typeof previewHtml === 'string' && previewHtml.trim().length > 0) {
    return previewHtml;
  }
  return PAYMENT_SCHEDULE_EDITOR_SAMPLE_HTML;
}
